import {
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromSeriousSystem from '@serious-system';
import { selectFavoriteAssistants } from '../../../favorites/favorites.selectors';
import { RouterService } from '../../shared/services/router.service';
import { ChatActions, ChatsState } from '../../store';
import * as fromTeammates from '../../teammates';
import { NavigationFavoritesListSmartComponent } from './navigation-favorites-list.smart.component';
import { NavigationTeammatesListSmartComponent } from './navigation-teammates-list.smart.component';

@Component({
  selector: 'squadbox-navigation-minimized-smart',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.AvatarDirective,
    NavigationTeammatesListSmartComponent,
    NavigationFavoritesListSmartComponent,
    RouterModule,
  ],
  template: `
    <!-- Note: The view transition name is necessary for z-indexing between pages. -->
    <div
      class="z-30 absolute p-4 h-full w-0 [view-transition-name:navigation-minimized]"
    >
      <div class="h-full flex flex-col gap-1">
        <div
          class="
            w-min
            flex items-center
            p-1 gap-1 rounded-2xl
            bg-shades-white border-neutral-200 border shadow-sm
          "
        >
          <button
            sdIconButton
            variant="icon"
            color="neutral"
            size="md"
            (click)="handleClickNavigationMaximizeCTA()"
          >
            <svg sdUseNavigationIcon="sidebar"></svg>
          </button>
          <button
            sdIconButton
            variant="icon"
            color="primary"
            size="md"
            class="w-10! rounded-lg!"
            (click)="handleClickNewChatCTA()"
          >
            <svg sdUseNavigationIcon="new-chat"></svg>
          </button>
          <button
            sdIconButton
            variant="icon"
            color="primary"
            size="sm"
            class="w-7! h-12! rounded-lg!"
            (click)="handleClickToggleDropdownCTA()"
          >
            <svg sdUseArrowIcon="chevron-small-down"></svg>
          </button>
        </div>

        @if (isDropdownOpened()) {
          <div
            #menu
            class="
              w-70
              flex flex-col items-center
              px-2 py-4 gap-4 rounded-2xl
              bg-shades-white border-neutral-200 border shadow-sm
              hide-scrollbar overflow-y-auto
            "
            animate.enter="fade-in"
            animate.leave="fade-out"
          >
            <squadbox-navigation-teammates-list-smart
              class="w-full"
              [teammatesList]="teammatesList()"
            />

            @if (favoritesList().length) {
              <squadbox-navigation-favorites-list-smart
                class="w-full"
                [favoritesList]="favoritesList()"
              />
            }
          </div>
        }
      </div>
    </div>
  `,
})
export class NavigationMinimizedSmartComponent {
  protected isDropdownOpened = signal(false);
  private readonly routerService = inject(RouterService);
  private readonly teammatesStore =
    inject<Store<fromTeammates.TeammatesState>>(Store);
  private readonly assistantsStore = inject(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);

  @ViewChild('menu') menu!: ElementRef<HTMLDivElement>;

  @HostListener('window:click')
  onWindowClick() {
    if (!this.menu) return;
    this.isDropdownOpened.set(false);
  }

  public readonly teammatesList = this.teammatesStore.selectSignal(
    fromTeammates.teammatesFeature.selectAllWithAssistants,
  );
  public readonly favoritesList = this.assistantsStore.selectSignal(
    selectFavoriteAssistants,
  );

  public handleClickNavigationMaximizeCTA() {
    this.chatsStore.dispatch(ChatActions.maximizeNavigation());
  }

  public async handleClickNewChatCTA() {
    await this.routerService.navigateToNewChat();
  }

  public handleClickToggleDropdownCTA() {
    this.isDropdownOpened.set(!this.isDropdownOpened());
  }
}
